export const project = 'stellantis'; // need this for when we dynamically import utils

const DEFAULT_TYPE = 'trigger';

// if on vdp use the first vehicle in vehicles array
// if not vdp but there are vehicles then get the full vehicle object from vehicles array
// otherwise use vehicle
// vehicle is most likely passed in as eventData.vehicle
// vehicles is most likely window.DDC.dataLayer.vehicles
const getVehicle = (vehicle = {}, vehicles = []) => {
	let v = vehicle;

	if (window?.DDC?.dataLayer?.page?.pageInfo?.isVdp) {
		[v] = vehicles;
	} else if (vehicles.length) {
		v = vehicles.find((veh) => veh?.uuid === vehicle?.uuid);
	}

	return v;
};

const isOffersPage = (alias) => {
	return (
		(!alias.includes('PARTS') && alias.includes('SPECIALS')) ||
		alias.includes('COUPON') ||
		alias.includes('INCENTIVES')
	);
};
const isFinancePage = (alias) => {
	return (
		(!isOffersPage(alias) && alias.includes('FINANCE')) ||
		alias.includes('FINANCING')
	);
};
const isTradeInPage = (alias) => {
	return alias.includes('TRADE') && !alias.includes('CONFIRM');
};
const isToolPage = (alias) => {
	return (
		alias.includes('INVENTORY_COMPARE') ||
		alias.includes('CALCULATOR_PAYMENT') ||
		isTradeInPage(alias) ||
		alias.includes('INVENTORY_FINDER')
	);
};
const isServicePage = (alias) => {
	return (
		(!isOffersPage(alias) && alias.includes('SERVICE')) ||
		alias.includes('PARTS') ||
		alias.includes('MOPAR') ||
		alias.includes('XTIME')
	);
};
const isHoursOrDirectionsPage = (alias) => {
	return alias.includes('HOURS') || alias.includes('DIRECTIONS');
};
const isAboutOrStaffPage = (alias) => {
	return (
		alias.includes('ABOUT') ||
		alias.includes('STAFF_LISTING') ||
		alias.includes('EMPLOYMENT_LISTING')
	);
};
const isCorporatePage = (alias) => {
	return (
		alias.includes('PRIVACY') ||
		alias.includes('LEGAL') ||
		alias.includes('SITEMAP')
	);
};

const inventoryTypeMapping = {
	1: 'used',
	2: 'new',
	3: 'cpo',
	NEW: 'new',
	USED: 'used',
	CERTIFIED: 'cpo',
	ALL: 'other',
	DEFAULT: ''
};

const mapInventoryType = (compositeType) =>
	inventoryTypeMapping[
		(typeof compositeType === 'string' && compositeType?.toUpperCase?.()) ||
			compositeType
	] || inventoryTypeMapping.DEFAULT;

function compositeTypeQueryFacet(queryFacets) {
	let val;

	// If present, use the compositeType
	if (queryFacets && queryFacets.compositeType) {
		return queryFacets.compositeType;
	}
	// Not present, attempt to guess from DDC page alias
	const pageAlias = window?.DDC?.dataLayer?.page?.pageInfo?.pageName;
	if (window?.DDC?.dataLayer?.page?.pageInfo?.isVlp) {
		if (pageAlias.includes('_NEW')) {
			val = 'new';
		} else if (pageAlias.includes('_CERTIFIED_USED')) {
			val = 'certified';
		} else if (pageAlias.includes('_USED')) {
			val = 'used';
		} else {
			val = 'all';
		}
	}

	return val;
}

export const sending = ({ data, event, preview }) => {
	const reportForEventViewer = {};
	const reportForNewRelic = {};

	let dl = 'digitalData';
	if (preview) {
		dl = 'previewDigitalData';
	}

	if (event === 'ddc.wstagging.first') {
		window[dl] = {
			...(window[dl] || {}),
			...data.digitalData,
			newEvent: (ev) => {
				const event_ = {
					attributes: {},
					eventAction: null,
					eventName: null,
					timeStamp: new Date(),
					type: null,
					...ev
				};

				// Event array is updated and a window.trigger is fired at this point to alert the browser that a new event
				// has occurred.
				window[dl].events.push(event_);
				if (typeof jQuery !== 'undefined') {
					jQuery(window).trigger({
						...event_,
						// if this is not passed there is a jquery error
						type: event_?.type || DEFAULT_TYPE
					});
				}

				return event_;
			}
		};

		reportForEventViewer.payload = window[dl];
		reportForEventViewer.sender = `window.${dl}`;
		reportForNewRelic.clientEventName = 'page load digitalData';
	} else {
		const {
			eventAction,
			eventName,
			type = DEFAULT_TYPE
		} = window[dl].newEvent(data);

		reportForEventViewer.payload = data;
		reportForEventViewer.sender = `window.${dl}.newEvent`;
		reportForNewRelic.clientEventName = `${type} - ${eventAction} ${eventName}`;
	}

	reportForNewRelic.sender = reportForEventViewer.sender;

	return {
		reportsForEventViewer: [reportForEventViewer],
		reportsForNewRelic: [reportForNewRelic]
	};
};

export function getFilteredSearchType({ eventData: { queryFacets } = {} }) {
	return mapInventoryType(compositeTypeQueryFacet(queryFacets));
}

export function getInventoryStatus({
	eventData: { vehicle = {} } = {},
	vehicles = []
}) {
	return {
		Live: 'On_The_Lot',
		live: 'On_The_Lot',
		'In Transit': 'In_Transit',
		in_transit: 'In_Transit',
		'In Transit At Factory': 'Being_Built',
		in_transit_at_factory: 'Being_Built',
		'On Deposit': 'Sale_Pending',
		on_deposit: 'Sale_Pending'
	}[getVehicle(vehicle, vehicles)?.status];
}

/**
 * Builds the pageName taxonomy required by FCA's specification
 */
export function buildPageName({ eventData, mapping }) {
	const { alias } = eventData;
	let pageName = '';
	let mappedAlias =
		mapping?.pagealias?.pageName || alias.toLowerCase().replace(/_/g, '-');

	// ----- Append the category information to the pageName ------
	if (window?.DDC?.dataLayer?.page?.pageInfo?.isVdp) {
		// Do Year and Model for VDP
		const year = window?.DDC?.dataLayer?.vehicles?.[0]?.modelYear;
		const model = window?.DDC?.dataLayer?.vehicles?.[0]?.model;

		pageName += `${year}:${model}:`;
	} else if (window?.DDC?.dataLayer?.page?.pageInfo?.isVlp) {
		// Do 'search-inventory' for VLP
		if (mappedAlias === 'all-inventory') {
			// all-inventory pages have their own override for the mapped alias based on the query facet
			const compositeType = mapInventoryType(
				compositeTypeQueryFacet(
					window?.DDC?.dataLayer?.page?.queryFacets
				)
			);

			pageName += `search-inventory:${compositeType}-inventory`;
			mappedAlias = '';
		} else {
			pageName += 'search-inventory:';
		}
	} else if (isFinancePage(alias)) {
		// Do 'financing' for any financing related pages
		pageName += 'financing:';
	} else if (isOffersPage(alias)) {
		// Do 'offers' - incentives pages
		pageName += 'offers:';
	} else if (isToolPage(alias)) {
		// Do 'tool' - payment calculator, comparing vehicles
		pageName += 'tool:';
	} else if (isServicePage(alias)) {
		// Do 'services' - schedule appointment and order parts
		pageName += 'services:';
	} else if (
		isHoursOrDirectionsPage(alias) ||
		isAboutOrStaffPage(alias) ||
		alias.includes('CONTACT')
	) {
		// Do 'dealer-information' - contact us, driving directions, maps, about us
		pageName += 'dealer-information:';
	} else if (isCorporatePage(alias)) {
		// Do 'corporate' for Privacy, Sitemap, Legal
		pageName += 'corporate:';
	} else if (alias.includes('SHOWROOM')) {
		// This appends the showroom make to the mappedAlias (ex: showroom:chrysler)
		// Also this relies on FCA having makes attached to body styles in the showroom.
		const showroomPath =
			window?.DDC?.dataLayer?.page?.pageInfo?.pagePath?.toLowerCase();
		const regexp = /(chrysler|dodge|jeep|ram)/g;
		const match = regexp.exec(showroomPath);

		if (match) {
			const theMake = match[0];
			// Append theMake to the Mapped Alias based on regex
			mappedAlias += `:${theMake}`;
		}
	}

	// ---- Append the mapped alias to the pageName (with category) -----
	return pageName + mappedAlias;
}

/**
 * Resolves the page type required by FCA spec
 *
 * @returns {string} the page type for the digitalData
 */
export function buildPageType({ eventData, mapping }) {
	let pageType = 'Other';

	if (
		// I think instead of next lines I could use double !! but since I'm not 100% sure, I'll go with tested code
		window?.DDC?.dataLayer?.page?.trackingTags?.pageType !== null &&
		window?.DDC?.dataLayer?.page?.trackingTags?.pageType !== undefined &&
		window?.DDC?.dataLayer?.page?.trackingTags?.pageType !== ''
	) {
		pageType = window?.DDC?.dataLayer?.page?.trackingTags?.pageType;
	} else if (mapping?.pagealias?.pageType) {
		pageType = mapping?.pagealias?.pageType;
	} else {
		const { alias } = eventData;
		// Add new mappings at the bottom
		if (alias.includes('SHOWROOM')) {
			// Check showroom url for a year
			const pathParts =
				window?.DDC?.dataLayer?.page?.pageInfo?.pagePath?.split('/');
			if (pathParts?.length > 2 && /^[0-9]{4}$/.test(pathParts[2])) {
				pageType = 'Model Showroom';
			} else {
				pageType = 'Showroom';
			}
		} else if (isOffersPage(alias)) {
			if (alias.includes('SERVICE')) {
				pageType = 'Service Specials';
			} else {
				pageType = 'Dealer Specials';
			}
		} else if (isFinancePage(alias)) {
			pageType = 'Finance';
		} else if (isServicePage(alias)) {
			if (alias.includes('SERVICE_LEAD') || alias.includes('XTIME')) {
				pageType = 'Schedule Service';
			} else if (alias.includes('PARTS_LEAD')) {
				pageType = 'Order Parts';
			} else if (alias.includes('PARTS')) {
				pageType = 'Parts';
			} else {
				pageType = 'Service';
			}
		}
	}

	return pageType;
}

export function getVehicleType({
	eventData: { vehicle = {} } = {},
	vehicles = []
} = {}) {
	const { inventoryType, newOrUsed } = getVehicle(vehicle, vehicles) || {};
	return mapInventoryType(
		inventoryType === 'certified' ? inventoryType : newOrUsed
	);
}

export function buildFilteredSearchFilters({
	eventData: { queryFacets = {} } = {}
}) {
	const filters = [];
	Object.keys(queryFacets).forEach((facet) => {
		// Exclude 'compositeType' and 'search' facets:
		// 	compositeType is used as a field in the events
		//	search is used for the typedSearch event and should not be included for filteredSearch
		if (facet !== 'compositeType' && facet !== 'search') {
			if (typeof queryFacets[facet] === 'string') {
				queryFacets[facet].split(',').forEach((v) => {
					filters.push(`${facet}: ${v}`);
				});
			} else {
				filters.push(`${facet}: ${queryFacets[facet]}`);
			}
		}
	});

	return filters.join('|');
}

// Per US1946646: an image counts as AI when the vehicle is In Transit, the
// vehicle has any images, and the image is not a DDC stock photo. DDC stock
// library images live at `images.dealer.com/ddc/...` (or
// `pictures.dealer.com/ddc/...`). Stellantis explicitly tells us we cannot
// differentiate AI from non-AI images directly; this proxy is the agreed
// approximation.
const isDDCStockImage = (uri = '') =>
	/(?:images|pictures)\.dealer\.com\/ddc\//i.test(uri);

const IN_TRANSIT_STATUSES = new Set([
	'In Transit',
	'in_transit',
	'In Transit At Factory',
	'in_transit_at_factory'
]);

const isInTransit = (status) => IN_TRANSIT_STATUSES.has(status);

const isAIVehicleImage = (image, vehicle) =>
	isInTransit(vehicle?.status) &&
	vehicle?.images?.length > 0 &&
	!isDDCStockImage(image?.uri);

export function getTotalAIVehicleImageCount({ vehicles = [] } = {}) {
	return vehicles.reduce(
		(sum, vehicle) =>
			sum +
			(vehicle?.images?.filter((img) => isAIVehicleImage(img, vehicle))
				.length || 0),
		0
	);
}

export function getVDPDefaultVehicleImageAI({ vehicles = [] } = {}) {
	if (!window?.DDC?.dataLayer?.page?.pageInfo?.isVdp) {
		return false;
	}
	const vehicle = vehicles[0];
	return isAIVehicleImage(vehicle?.images?.[0], vehicle);
}

export function getVehicleDataForAttributes({
	eventData: { vehicle },
	vehicles = []
}) {
	const veh = vehicle || vehicles[0];
	let vehicleData = {};

	if (veh && Object.keys(veh).length) {
		vehicleData = {
			inventoryStatus: '[[fn.getInventoryStatus]]',
			make: veh.make,
			model: veh.model,
			status: getVehicleType({
				eventData: {
					vehicle: veh
				},
				vehicles
			}),
			trim: veh.trim,
			vin: veh.vin,
			year: veh.modelYear || veh.year
		};
	}

	return vehicleData;
}
